/**
 * Subscription Service
 * Handles RevenueCat integration and subscription management
 */
import { Purchases, LOG_LEVEL, PRODUCT_CATEGORY } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';

// RevenueCat API Keys - platform specific
// iOS: appl_xxx, Android: goog_xxx
const REVENUECAT_IOS_API_KEY = 'appl_ZzTEKUoSGdbXhcCQamdpPRJjafg';
const REVENUECAT_ANDROID_API_KEY = 'goog_KXjWBkrxxDHpvRtGUzEiEDEyOuC';

// Get the correct API key based on platform
function getRevenueCatApiKey() {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') {
    return REVENUECAT_IOS_API_KEY;
  } else if (platform === 'android') {
    return REVENUECAT_ANDROID_API_KEY;
  }
  return null;
}

// Product IDs - must match App Store Connect / Google Play Console.
// LIVE identifiers confirmed against the RevenueCat "Fitxess Pro" entitlement:
//   iOS monthly = fitxess_pro_monthly, iOS annual = Fitxess_Pro_Annual.
// (Purchases resolve via the RC Offering's package types — these strings are
// used for the web mock + as a substring fallback in findPackage.)
export const PRODUCTS = {
  PLUS_MONTHLY: 'fitxess_pro_monthly',
  PLUS_ANNUAL: 'Fitxess_Pro_Annual',
};

// Subscription tiers
export const TIERS = {
  FREE: 'free',
  PLUS: 'plus',
  PRO: 'pro',
};

// RevenueCat purchase error codes (string-typed per RC SDK).
// See: @revenuecat/purchases-typescript-internal-esm/errors.d.ts
export const RC_ERROR_CODES = {
  PURCHASE_CANCELLED: '1',
  PRODUCT_ALREADY_PURCHASED: '6',
  RECEIPT_ALREADY_IN_USE: '7',
};

/**
 * True when the StoreKit/RevenueCat error indicates the user already owns
 * this subscription (e.g., Apple's "You're currently subscribed to this"
 * dialog). Codes can come back as numbers OR strings depending on platform,
 * and the human-readable code may also be present, so we check defensively.
 */
export function isAlreadySubscribedError(error) {
  if (!error) return false;
  const code = String(error.code ?? '');
  if (code === RC_ERROR_CODES.PRODUCT_ALREADY_PURCHASED) return true;
  if (code === RC_ERROR_CODES.RECEIPT_ALREADY_IN_USE) return true;
  const readable = (
    error.readableErrorCode ||
    error.userInfo?.readableErrorCode ||
    ''
  ).toString().toUpperCase();
  if (readable.includes('PRODUCT_ALREADY_PURCHASED')) return true;
  if (readable.includes('RECEIPT_ALREADY_IN_USE')) return true;
  const msg = (error.message || '').toLowerCase();
  if (msg.includes('already') && msg.includes('subscrib')) return true;
  if (msg.includes('already purchased')) return true;
  return false;
}

export function isUserCancelledError(error) {
  if (!error) return false;
  if (error.userCancelled) return true;
  const code = String(error.code ?? '');
  if (code === RC_ERROR_CODES.PURCHASE_CANCELLED) return true;
  return false;
}

// Feature flags for entitlements
export const FEATURES = {
  CAN_GENERATE_WEEKLY_PLAN: 'can_generate_weekly_plan',
  CAN_SAVE_PLANS: 'can_save_plans',
  CAN_REGENERATE_UNLIMITED: 'can_regenerate_unlimited',
  CAN_ACCESS_GROCERY_LIST: 'can_access_grocery_list',
  CAN_CUSTOMIZE_MACROS: 'can_customize_macros',
  CAN_EDIT_PLANS: 'can_edit_plans',
  CAN_ACCESS_FOOD_NEARBY: 'can_access_food_nearby',
};

let isInitialized = false;

/**
 * Initialize RevenueCat SDK
 * Must be called before any other subscription operations
 */
export async function initializeRevenueCat(userId) {
  // Only initialize on native platforms
  if (!Capacitor.isNativePlatform()) {
    console.log('[RevenueCat] Skipping initialization - not native platform');
    return false;
  }

  if (isInitialized) {
    console.log('[RevenueCat] Already initialized');
    return true;
  }

  const apiKey = getRevenueCatApiKey();
  if (!apiKey) {
    console.error('[RevenueCat] No API key configured for this platform');
    return false;
  }

  try {
    await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
    
    await Purchases.configure({
      apiKey: apiKey,
      appUserID: userId || null, // null for anonymous users
    });

    isInitialized = true;
    console.log('[RevenueCat] Initialized successfully with platform:', Capacitor.getPlatform());
    return true;
  } catch (error) {
    console.error('[RevenueCat] Initialization failed:', error);
    return false;
  }
}

/**
 * Identify user with RevenueCat
 * Links purchases to user account
 */
export async function identifyUser(userId) {
  if (!Capacitor.isNativePlatform()) {
    return { customerInfo: null };
  }

  try {
    const result = await Purchases.logIn({ appUserID: userId });
    console.log('[RevenueCat] User identified:', userId);
    return result;
  } catch (error) {
    console.error('[RevenueCat] Failed to identify user:', error);
    throw error;
  }
}

/**
 * Set the user's verified email as RevenueCat's reserved `$email` customer
 * attribute. This is what makes a Fitxess customer searchable by email in the
 * RevenueCat dashboard (Customers) so promotional "Fitxess Pro" entitlements
 * can be granted/revoked by email. We send ONLY the email — no other PII.
 *
 * Must be called AFTER Purchases.configure/logIn. No-op on web or when the
 * email is missing.
 */
export async function setUserEmail(email) {
  if (!Capacitor.isNativePlatform()) return;
  const trimmed = (email || '').trim();
  if (!trimmed) return;
  try {
    await Purchases.setEmail({ email: trimmed });
    console.log('[RevenueCat] Email attribute set for customer.');
  } catch (error) {
    // Non-fatal: attribute sync failure must never block the app.
    console.warn('[RevenueCat] Failed to set email attribute:', error);
  }
}

/**
 * Log out user from RevenueCat
 */
export async function logoutUser() {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    await Purchases.logOut();
    console.log('[RevenueCat] User logged out');
  } catch (error) {
    console.error('[RevenueCat] Failed to log out:', error);
  }
}

/**
 * Get available subscription offerings
 * Returns products with pricing info
 */
export async function getOfferings() {
  if (!Capacitor.isNativePlatform()) {
    // WEB-PREVIEW-ONLY mock. This is NEVER used on a real device — native
    // builds read live prices/offers from RevenueCat/the stores. These numbers
    // are illustrative of the intended post-change pricing for preview only and
    // do not reflect or change any live App Store / Google Play configuration.
    // introPrice models a 3-day free trial so the trial UI can be previewed.
    const introPrice = {
      price: 0,
      priceString: 'Free',
      period: 'P3D',
      periodUnit: 'DAY',
      periodNumberOfUnits: 3,
      cycles: 1,
    };
    return {
      current: {
        identifier: 'default',
        availablePackages: [
          {
            identifier: 'monthly',
            product: {
              identifier: PRODUCTS.PLUS_MONTHLY,
              priceString: '$8.99',
              price: 8.99,
              title: 'Fitxess Pro Monthly',
              description: 'Full access to all features',
              introPrice,
            },
          },
          {
            identifier: 'annual',
            product: {
              identifier: PRODUCTS.PLUS_ANNUAL,
              priceString: '$59.99',
              price: 59.99,
              title: 'Fitxess Pro Annual',
              description: 'Full access - Best value',
              introPrice,
            },
          },
        ],
      },
    };
  }

  try {
    const offerings = await Purchases.getOfferings();
    console.log('[RevenueCat] Offerings:', offerings);
    return offerings;
  } catch (error) {
    console.error('[RevenueCat] Failed to get offerings:', error);
    throw error;
  }
}

/**
 * Retrieve introductory-offer (free-trial) eligibility per product.
 *
 * iOS: RevenueCat returns a real per-product eligibility status.
 * Android: RevenueCat always returns UNKNOWN — Google determines actual
 *   eligibility at purchase time; callers should present the offer when the
 *   product exposes a free phase.
 *
 * Returns a map: { [productId]: { status: INTRO_ELIGIBILITY_STATUS } }
 * where status 2 = ELIGIBLE, 1 = INELIGIBLE, 3 = NO_INTRO_OFFER, 0 = UNKNOWN.
 *
 * WEB PREVIEW: honors window.__FITXESS_TRIAL_SIM__ ('eligible'|'ineligible')
 * so both paywall states can be previewed/screenshotted off-device.
 */
export async function getIntroEligibility(productIds = []) {
  if (!Capacitor.isNativePlatform()) {
    if (typeof window !== 'undefined' && window.__FITXESS_TRIAL_SIM__) {
      const status = window.__FITXESS_TRIAL_SIM__ === 'eligible' ? 2 : 1;
      return Object.fromEntries((productIds || []).map((id) => [id, { status }]));
    }
    return {};
  }
  try {
    const res = await Purchases.checkTrialOrIntroductoryPriceEligibility({
      productIdentifiers: productIds,
    });
    return res || {};
  } catch (error) {
    console.warn('[RevenueCat] intro eligibility check failed:', error);
    return {};
  }
}

/**
 * Get current customer info (subscription status)
 */
export async function getCustomerInfo() {
  if (!Capacitor.isNativePlatform()) {
    return { customerInfo: null };
  }

  try {
    const result = await Purchases.getCustomerInfo();
    console.log('[RevenueCat] Customer info:', result);
    return result;
  } catch (error) {
    console.error('[RevenueCat] Failed to get customer info:', error);
    throw error;
  }
}

/**
 * Purchase a package (subscription)
 */
export async function purchasePackage(packageToPurchase) {
  if (!Capacitor.isNativePlatform()) {
    throw new Error('Purchases only available on native platforms');
  }

  try {
    const result = await Purchases.purchasePackage({
      aPackage: packageToPurchase,
    });
    console.log('[RevenueCat] Purchase successful:', result);
    return result;
  } catch (error) {
    console.error('[RevenueCat] Purchase failed:', error);
    
    // Check if user cancelled
    if (error.userCancelled) {
      throw new Error('Purchase cancelled');
    }
    
    throw error;
  }
}

/**
 * Restore previous purchases
 */
export async function restorePurchases() {
  if (!Capacitor.isNativePlatform()) {
    return { customerInfo: null };
  }

  try {
    const result = await Purchases.restorePurchases();
    console.log('[RevenueCat] Purchases restored:', result);
    return result;
  } catch (error) {
    console.error('[RevenueCat] Failed to restore purchases:', error);
    throw error;
  }
}

/**
 * Normalize an entitlement identifier so lookups tolerate spaces, casing, and
 * punctuation. "Fitxess Pro" → "fitxesspro", "fitxess_pro" → "fitxesspro", etc.
 */
function normalizeEntitlementKey(key) {
  return String(key || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Any of these (after normalization) are treated as an active paid entitlement.
// The RC entitlement identifier in production is "Fitxess Pro" ("fitxesspro").
// Legacy aliases are kept so older RC configurations still resolve.
const PAID_ENTITLEMENT_KEYS = new Set([
  'fitxesspro',
  'pro',
  'plus',
  'premium',
]);

/**
 * True if customerInfo contains at least one active paid entitlement
 * recognized by the app (see PAID_ENTITLEMENT_KEYS above).
 */
export function hasPaidEntitlement(customerInfo) {
  const activeEnts = customerInfo?.entitlements?.active;
  if (!activeEnts) return false;
  for (const key of Object.keys(activeEnts)) {
    if (PAID_ENTITLEMENT_KEYS.has(normalizeEntitlementKey(key))) return true;
  }
  return false;
}

/**
 * Check if user has active subscription
 */
export function hasActiveSubscription(customerInfo) {
  return hasPaidEntitlement(customerInfo);
}

/**
 * Get subscription tier from customer info.
 * Single paid tier: any recognized paid entitlement → TIER_PLUS (legacy internal
 * alias for the Fitxess Pro plan). No Plus-vs-Pro split in the current product.
 */
export function getSubscriptionTier(customerInfo) {
  return hasPaidEntitlement(customerInfo) ? TIERS.PLUS : TIERS.FREE;
}

/**
 * Subscribe to RevenueCat CustomerInfo updates. Returns a callbackId that
 * can be passed to removeCustomerInfoUpdateListener for cleanup.
 *
 * This is critical for the iOS "already subscribed" flow: when StoreKit
 * recognises the user owns the SKU and shows its built-in alert, the
 * outstanding purchase promise can hang — but the SDK still pushes a
 * fresh customerInfo through this listener once the user dismisses the
 * alert. Listening lets us unlock the app even if purchase() never resolves.
 */
export async function addCustomerInfoUpdateListener(listener) {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const callbackId = await Purchases.addCustomerInfoUpdateListener(listener);
    return callbackId;
  } catch (error) {
    console.error('[RevenueCat] Failed to add customerInfo listener:', error);
    return null;
  }
}

export async function removeCustomerInfoUpdateListener(listenerToRemove) {
  if (!Capacitor.isNativePlatform() || !listenerToRemove) return;
  try {
    await Purchases.removeCustomerInfoUpdateListener({ listenerToRemove });
  } catch (error) {
    console.error('[RevenueCat] Failed to remove customerInfo listener:', error);
  }
}

/**
 * Open subscription management (App Store / Play Store)
 */
export async function openSubscriptionManagement() {
  if (!Capacitor.isNativePlatform()) {
    // For web, link to appropriate store
    window.open('https://apps.apple.com/account/subscriptions', '_blank');
    return;
  }

  try {
    // RevenueCat doesn't have a direct method, but we can use native URLs
    const platform = Capacitor.getPlatform();
    
    if (platform === 'ios') {
      // iOS subscription management URL
      window.open('https://apps.apple.com/account/subscriptions', '_blank');
    } else if (platform === 'android') {
      // Android subscription management URL
      window.open('https://play.google.com/store/account/subscriptions', '_blank');
    }
  } catch (error) {
    console.error('[RevenueCat] Failed to open subscription management:', error);
  }
}

export default {
  initializeRevenueCat,
  identifyUser,
  setUserEmail,
  getIntroEligibility,
  logoutUser,
  getOfferings,
  getCustomerInfo,
  purchasePackage,
  restorePurchases,
  hasActiveSubscription,
  hasPaidEntitlement,
  getSubscriptionTier,
  openSubscriptionManagement,
  addCustomerInfoUpdateListener,
  removeCustomerInfoUpdateListener,
  isAlreadySubscribedError,
  isUserCancelledError,
  RC_ERROR_CODES,
  PRODUCTS,
  TIERS,
  FEATURES,
};
